// Container Trader delivery & fit checker: specs, site requirements and prep notes.
// Plain data and helpers (no React) so they are easy to test and keep in sync.
//
// Clearance figures are planning numbers for tilt-bed delivery, the most common method.
// They match what US container dealers publish, but every site and truck is different:
// the driver or dispatcher confirms the real requirements when delivery is booked.

import { sizeFromTitle } from '../../containers/StatePage/ctStatePages';

export const SIZES = [
  {
    id: '20',
    label: "20' Standard",
    exterior: "20' L × 8' W × 8'6\" H",
    capacity: '~1,170 cu ft',
    tare: '~4,800–5,100 lbs empty',
    straightRun: '60–125 ft',
    straightRunNote:
      "About 75 ft on a rollback. If no rollback is available, your 20' may come on a 40' or longer tilt trailer, which needs 100–125 ft.",
    overheadAtDrop: '16 ft',
    truck: "Rollback / tilt-bed truck when available; otherwise a semi with a 40' or longer tilt trailer",
    highCube: false,
    search: 'pub_listingType=20',
  },
  {
    id: '20hc',
    label: "20' High Cube",
    exterior: "20' L × 8' W × 9'6\" H",
    capacity: '~1,360 cu ft',
    tare: '~5,000–5,300 lbs empty',
    straightRun: '60–125 ft',
    straightRunNote:
      "About 75 ft on a rollback. If no rollback is available, your 20' may come on a 40' or longer tilt trailer, which needs 100–125 ft.",
    overheadAtDrop: '17 ft',
    truck: "Rollback / tilt-bed truck when available; otherwise a semi with a 40' or longer tilt trailer",
    highCube: true,
    search: 'pub_listingType=20hc',
  },
  {
    id: '40',
    label: "40' Standard",
    exterior: "40' L × 8' W × 8'6\" H",
    capacity: '~2,390 cu ft',
    tare: '~8,000–8,400 lbs empty',
    straightRun: '100–125 ft',
    straightRunNote: 'The truck drives forward the full length while the container slides off.',
    overheadAtDrop: '16 ft',
    truck: 'Semi-tractor with a 40–48 ft tilt-bed trailer',
    highCube: false,
    search: 'pub_listingType=40',
  },
  {
    id: '40hc',
    label: "40' High Cube",
    exterior: "40' L × 8' W × 9'6\" H",
    capacity: '~2,690 cu ft',
    tare: '~8,500–8,900 lbs empty',
    straightRun: '100–125 ft',
    straightRunNote: 'The truck drives forward the full length while the container slides off.',
    overheadAtDrop: '17 ft',
    truck: 'Semi-tractor with a 40–48 ft tilt-bed trailer',
    highCube: true,
    search: 'pub_listingType=40hc',
  },
  {
    id: '45hc',
    label: "45' High Cube",
    exterior: "45' L × 8' W × 9'6\" H",
    capacity: '~3,040 cu ft',
    tare: '~10,000–10,600 lbs empty',
    straightRun: '125–140 ft',
    straightRunNote: 'Longest run of any size: confirm access with dispatch before ordering.',
    overheadAtDrop: '17 ft',
    truck: 'Semi-tractor with a 48–53 ft tilt-bed trailer',
    highCube: true,
    search: 'pub_listingType=45hc',
  },
];

// Applies to every size: the bed tilts up as the container slides off.
export const PATH_WIDTH = '12 ft minimum (14 ft is comfortable)';
export const OVERHEAD_ON_ROUTE = '14 ft along the whole route in';

// Condition grades used on the site (categoryLevel1 ids).
export const CONDITIONS = [
  {
    id: '1trip',
    label: 'One-Trip / New',
    bestFor: 'Retail, offices, homes, modifications, anywhere looks matter',
    note:
      'Used for one shipment, so it is the newest grade available and usually mostly free of dents and scratches. It is not factory-perfect: on that trip it was loaded by forklift, stacked next to other containers on the ship, moved by truck or rail, unloaded at the receiver and stacked again at the depot. Minor scuffs, small dents, paint rub-off from other containers and light floor scratches can happen along the way.',
  },
  {
    id: 'iicl',
    label: 'IICL',
    bestFor: 'Buyers who want a high-grade used box for storage or shipping',
    note:
      'Used, but meets strict leasing-company standards: minimal dents and rust, and solid floors and doors.',
  },
  {
    id: 'cw',
    label: 'Cargo Worthy (CW)',
    bestFor: 'Shipping goods overseas or by rail, or heavy-duty storage',
    note:
      'Structurally sound for carrying cargo, but expect dents and surface rust. If you plan to ship goods, ask the seller about current CSC certification.',
  },
  {
    id: 'wwt',
    label: 'Wind & Water Tight (WWT)',
    bestFor: 'Dry storage on farms, job sites and homes at the lowest price',
    note:
      'Keeps wind and water out, but is not certified for shipping cargo. Expect dents, rust and faded paint.',
  },
  {
    id: 'asis',
    label: 'As-Is',
    bestFor: 'Budget projects, parts or uses where holes do not matter',
    note:
      'Sold in its current state and may have holes, leaks or door problems. Check the photos closely or ask the seller before buying.',
  },
];

export const SURFACES = [
  {
    id: 'hard',
    label: 'Concrete / paved / asphalt',
    status: 'ok',
    title: 'Ideal',
    note: 'Level, firm footing with no settling. Check that the slab can take the truck as well.',
  },
  {
    id: 'gravel',
    label: 'Compacted gravel / hard-pack',
    status: 'ok',
    title: 'Good to go',
    note:
      'Drains well and holds the container. Make sure the spot is reasonably level so the doors open and close smoothly.',
  },
  {
    id: 'dirt',
    label: 'Soft dirt / field / grass',
    status: 'warn',
    title: 'Site prep needed',
    note:
      'Soft ground can let the container sink and the doors bind, and a loaded truck can get stuck after rain. Put treated 4×4 or 6×6 timbers, concrete blocks or a gravel pad under the four corners, and plan delivery for dry weather.',
  },
  {
    id: 'slope',
    label: 'Sloped or uneven ground',
    status: 'warn',
    title: 'Level it first',
    note:
      'A container that is out of level racks its frame and the doors may not close. Level the spot or build up the low corners with blocks before delivery day.',
  },
];

export const SITE_CHECKS = [
  { id: 'run', label: 'A straight, clear run at least as long as shown above' },
  { id: 'width', label: 'At least 12 ft of width at every gate, turn and pinch point' },
  { id: 'overhead', label: 'No wires, branches or eaves under the overhead clearance shown' },
  { id: 'doors', label: 'Room in front of the doors to swing them fully open (about 8 ft)' },
  { id: 'permit', label: 'Checked local, HOA or landlord rules for placing a container' },
];

export const DEFAULT_SIZE = '40';
export const DEFAULT_CONDITION = 'wwt';
export const DEFAULT_SURFACE = 'gravel';

export const sizeById = id =>
  SIZES.find(s => s.id === id) || SIZES.find(s => s.id === DEFAULT_SIZE);
export const conditionById = id =>
  CONDITIONS.find(c => c.id === id) || CONDITIONS.find(c => c.id === DEFAULT_CONDITION);
export const surfaceById = id =>
  SURFACES.find(s => s.id === id) || SURFACES.find(s => s.id === DEFAULT_SURFACE);

const SIZE_ID_BY_LABEL = SIZES.reduce((acc, s) => ({ ...acc, [s.label]: s.id }), {});

/** Size id for a listing: from its listing type ('40hc'), else from its title ("40ft HC"). */
export const sizeIdForListing = listing => {
  const publicData = listing?.attributes?.publicData || {};
  if (SIZES.some(s => s.id === publicData.listingType)) return publicData.listingType;
  const fromTitle = sizeFromTitle(listing?.attributes?.title);
  return SIZE_ID_BY_LABEL[fromTitle] || null;
};

/** Condition id for a listing, when its category is one of the condition grades. */
export const conditionIdForListing = listing => {
  const category = listing?.attributes?.publicData?.categoryLevel1;
  return CONDITIONS.some(c => c.id === category) ? category : null;
};

/** Everything the results panel shows for one combination of choices. */
export const deliveryProfile = ({ size, condition, surface }) => {
  const s = sizeById(size);
  return {
    size: s,
    condition: conditionById(condition),
    surface: surfaceById(surface),
    pathWidth: PATH_WIDTH,
    overheadOnRoute: OVERHEAD_ON_ROUTE,
    overheadAtDrop: s.highCube
      ? `${s.overheadAtDrop} at the drop spot (high cubes stand a foot taller)`
      : `${s.overheadAtDrop} at the drop spot`,
  };
};
